import React, { useEffect, useState } from "react";
import moment from "moment";
import URL from "../../../../Main/url";
import CurrencyWord from "../../../../Componets/Currencywords";
import formatDateDDMMYYYY from "../../../../Main/formatDate";
import UserCurrency from "../../../../Componets/UserCurrency";

const A4Invoice = React.forwardRef((props, ref) => {
    const { values } = props;

    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;

    const capitalizeWords = (str) => {
        if (!str) return "";
        return str.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const calculateTotalPages = () => {
        if (!values?.details) return 1;
        return Math.ceil(values.details.length / itemsPerPage);
    };

    const getItemsForPage = (page) => {
        if (!values?.details) return [];
        const startIndex = (page - 1) * itemsPerPage;
        return values.details.slice(startIndex, startIndex + itemsPerPage);
    };

    useEffect(() => {
        if (values?.details) {
            setTotalPages(calculateTotalPages());
        }
    }, [values?.details]);

    const injectScopedStyles = () => {
        const style = document.createElement("style");
        style.innerHTML = `
      @page {
        size: A4;
        margin: 3% 6% 5% 6%;
      }

      @media print {
        .bkg-header-section {
          position: fixed;
          top: 0%; left: 0%; right: 0%;
          background: white;
          z-index: 1000;
          page-break-inside: avoid;
        }
        .bkg-table-header-section {
          position: fixed;
          top: 28%; left: 0%; right: 0%;
          background: white;
          z-index: 999;
          page-break-inside: avoid;
        }
        .bkg-invoice-content { margin-top: 46%; }
        .bkg-page-break { page-break-before: always; margin-top: 46%; }
        .bkg-footer {
          font-size: 12px; white-space: pre;
          position: fixed; bottom: 10mm; left: 6%;
        }
        .bkg-footer-right {
          font-size: 12px; white-space: pre;
          position: fixed; bottom: 10mm; right: 6%;
        }
        .bkg-invoice-table { table-layout: fixed; width: 100%; }
        .bkg-col-sno    { width: 5%; }
        .bkg-col-desc   { width: 25%; }
        .bkg-col-batch  { width: 12%; }
        .bkg-col-unit   { width: 6%; }
        .bkg-col-qty    { width: 6%; }
        .bkg-col-price  { width: 10%; }
        .bkg-col-vat    { width: 6%; }
        .bkg-col-vatamt { width: 12%; }
        .bkg-col-total  { width: 10%; }
      }
    `;
        document.head.appendChild(style);
    };

    useEffect(() => {
        if (values?.branch?.name) injectScopedStyles();
    }, [values?.branch?.name]);

    const totalDeposited =
        values?.deposits?.reduce((sum, d) => sum + parseFloat(d?.amount || 0), 0) || 0;

    const remaining = Math.max(0, parseFloat(values?.total || 0) - totalDeposited);

    // ── Header ───────────────────────────────────────────────────────────────
    const renderHeaderSection = () => (
        <div className="bkg-header-section">
            <div className="flex justify-between items-start">
                <div className="text-black-900 text-[0.65rem]">
                    <div className="text-bold text-md">{capitalizeWords(values?.branch?.name)}</div>
                    <div className="text-medium text-xs">
                        Email&nbsp;:&nbsp;{values?.branch?.email || ""}&nbsp;
                    </div>
                    <div className="text-medium text-xs">
                        Phone&nbsp;:&nbsp;{values?.branch?.phone || ""}&nbsp;
                    </div>
                    <div className="text-xs">
                        {values?.branch?.area ? values?.branch?.area + ", " : ""}
                        {values?.branch?.city ? values?.branch?.city + ", " : ""}
                        {values?.branch?.country || ""}
                    </div>
                    <div className="text-xs">
                        VATIN&nbsp;:&nbsp;{values?.branch?.tax || "N/A"}
                    </div>
                </div>

                {values?.branch?.image ? (
                    <div className="flex justify-center flex-col items-center">
                        <img
                            className="w-[50px] h-[50px]"
                            src={`${URL}/uploads${values?.branch?.image}`}
                            alt="Branch Logo"
                        />
                        <div className="text-bold text-lg">{capitalizeWords(values?.branch?.name)}</div>
                    </div>
                ) : (
                    <div className="flex justify-center flex-col items-center">
                        <div className="w-[50px] h-[50px] rounded-md bg-gray-300 text-black flex items-center justify-center text-xl font-semibold">
                            {values?.branch?.name?.charAt(0)?.toUpperCase() || "B"}
                        </div>
                        <div className="text-bold text-lg">{capitalizeWords(values?.branch?.name)}</div>
                    </div>
                )}
            </div>

            <hr className="my-2 border-black" />
            <div className="text-medium text-md text-center py-1">BOOKING CONFIRMATION</div>
            <hr className="my-2 border-black" />

            <div className="border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex justify-between">
                    {/* Customer info */}
                    <div>
                        <div className="font-semibold text-[0.70rem] mb-2">Customer Name</div>
                        <div className="text-[0.65rem] space-y-1">
                            <div className="font-medium">
                                {values?.customer?.name || "Walk-in Customer"}
                            </div>
                            {values?.customer?.phone && (
                                <div>
                                    <span className="font-semibold">Phone :</span>{" "}
                                    {values?.customer?.phone}
                                </div>
                            )}
                            {values?.customer?.email && (
                                <div>
                                    <span className="font-semibold">Email :</span>{" "}
                                    {values?.customer?.email}
                                </div>
                            )}
                            <div>
                                {values?.customer?.city ? values?.customer?.city + ", " : ""}
                                {values?.customer?.state}
                            </div>
                            <div>{values?.customer?.country}</div>
                            {values?.customer?.tax && (
                                <div>
                                    <span className="font-semibold">VATIN :</span>{" "}
                                    {values?.customer?.tax}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Booking meta */}
                    <div className="text-[0.65rem] space-y-1">
                        <div className="flex">
                            <div className="w-28 text-bold">Booking No</div>
                            <div className="px-2">:</div>
                            <div>#BKG-{values?.number}</div>
                        </div>
                        <div className="flex">
                            <div className="w-28 text-bold">Date</div>
                            <div className="px-2">:</div>
                            <div>{formatDateDDMMYYYY(values?.date)}</div>
                        </div>
                        {values?.expiry_date && (
                            <div className="flex">
                                <div className="w-28 text-bold">Valid Until</div>
                                <div className="px-2">:</div>
                                <div>{formatDateDDMMYYYY(values?.expiry_date)}</div>
                            </div>
                        )}
                        {values?.salesman?.name && (
                            <div className="flex">
                                <div className="w-28 text-bold">Salesman</div>
                                <div className="px-2">:</div>
                                <div>{values?.salesman?.name}</div>
                            </div>
                        )}
                        <div className="flex">
                            <div className="w-28 text-bold">Status</div>
                            <div className="px-2">:</div>
                            <div className="capitalize">{values?.status || "—"}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ── Table headers ────────────────────────────────────────────────────────
    const renderTableHeaders = () => (
        <div className="bkg-table-header-section">
            <table className="bkg-invoice-table w-full border-separate border-spacing-0 rounded-t-lg overflow-hidden border border-gray-300 mb-0">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="bkg-col-sno text-[0.60rem] text-left py-2 pl-3 border-b border-gray-300 rounded-tl-lg">S.NO</th>
                        <th className="bkg-col-desc text-[0.60rem] text-left p-1 border-b border-gray-300">DESCRIPTION</th>
                        <th className="bkg-col-batch text-[0.60rem] text-left p-1 border-b border-gray-300">BATCH / EXPIRY</th>
                        <th className="bkg-col-unit text-[0.60rem] text-center p-1 border-b border-gray-300">UNIT</th>
                        <th className="bkg-col-qty text-[0.60rem] text-center p-1 border-b border-gray-300">QTY</th>
                        <th className="bkg-col-price text-[0.60rem] text-center p-1 border-b border-gray-300">PRICE</th>
                        <th className="bkg-col-vat text-[0.60rem] text-center p-1 border-b border-gray-300">VAT%</th>
                        <th className="bkg-col-vatamt text-[0.60rem] text-center p-1 border-b border-gray-300">VAT Amount</th>
                        <th className="bkg-col-total text-[0.60rem] text-center pr-1 border-b border-gray-300 rounded-tr-lg">TOTAL</th>
                    </tr>
                </thead>
            </table>
        </div>
    );

    // ── Table body per page ──────────────────────────────────────────────────
    const renderTableBody = (pageItems, pageNumber, isLastPage) => (
        <div
            key={`page-${pageNumber}`}
            className={pageNumber > 1 ? "bkg-page-break" : "bkg-invoice-content"}
        >
            <table className="bkg-invoice-table w-full border-separate border-spacing-0 overflow-hidden border border-gray-300 border-t-0">
                <tbody className="text-sm">
                    {pageItems.map((value, index) => {
                        const globalIndex = (pageNumber - 1) * itemsPerPage + index;
                        const isLastOverallRow = isLastPage && index === pageItems.length - 1;

                        return (
                            <tr key={`${pageNumber}-${index}`} className="hover:bg-gray-50">
                                <td className={`bkg-col-sno text-[0.60rem] p-1 pl-3 ${isLastOverallRow ? "rounded-bl-lg" : ""}`}>
                                    {globalIndex + 1}
                                </td>
                                <td className="bkg-col-desc text-[0.60rem] p-1">
                                    <div>{value?.name}</div>
                                </td>
                                <td className="bkg-col-batch text-[0.60rem] p-1">
                                    <div>{value?.description?.number || value?.batch || ""}</div>
                                    <div>
                                        {value?.expiry_date ? formatDateDDMMYYYY(value?.expiry_date) : ""}
                                    </div>
                                </td>
                                <td className="bkg-col-unit text-center text-[0.60rem] p-1">
                                    {value?.unit_name}
                                </td>
                                <td className="bkg-col-qty text-center text-[0.60rem] p-1">
                                    {value?.quantity}
                                </td>
                                <td className="bkg-col-price text-center text-[0.60rem] p-1">
                                    {parseFloat(value?.sale_price || 0).toFixed(3)}
                                </td>
                                <td className="bkg-col-vat text-center text-[0.60rem] p-1">
                                    {value?.tax}%
                                </td>
                                <td className="bkg-col-vatamt text-center text-[0.60rem] p-1 text-gray-500">
                                    {parseFloat(value?.tax_amount || 0).toFixed(3)}
                                </td>
                                <td className={`bkg-col-total text-center text-[0.60rem] p-1 pr-2 ${isLastOverallRow ? "rounded-br-lg" : ""}`}>
                                    {parseFloat(value?.total || 0).toFixed(3)}
                                </td>
                            </tr>
                        );
                    })}

                    {/* Empty rows to fill page */}
                    {pageItems.length < itemsPerPage &&
                        Array.from({ length: itemsPerPage - pageItems.length }).map((_, i, arr) => {
                            const isLastEmptyRow = isLastPage && i === arr.length - 1;
                            return (
                                <tr key={`empty-${pageNumber}-${i}`} className="hover:bg-gray-50">
                                    <td className={`text-[0.60rem] p-2 pl-3 ${isLastEmptyRow ? "rounded-bl-lg" : ""}`}>&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className="p-2">&nbsp;</td>
                                    <td className={`p-1 pr-2 ${isLastEmptyRow ? "rounded-br-lg" : ""}`}>&nbsp;</td>
                                </tr>
                            );
                        })}
                </tbody>
            </table>

            {/* Totals + deposits — only on last page */}
            {isLastPage && (
                <>
                    <div className="border border-gray-300 rounded-lg p-4 mb-4 mt-4 shadow-sm">
                        <div className="flex justify-between">
                            {/* Total in words */}
                            <div className="text-[0.65rem] w-1/2">
                                <div className="font-semibold mb-2 text-[0.70rem]">
                                    Total in words&nbsp;:&nbsp;
                                    <span className="font-normal">
                                        <CurrencyWord amount={parseFloat(values?.total || 0).toFixed(3)} />
                                    </span>
                                </div>
                            </div>

                            {/* Totals table */}
                            <div className="text-[0.65rem] w-1/2">
                                <table className="w-full text-[0.65rem]">
                                    <tbody>
                                        <tr>
                                            <td className="text-right pr-2 py-1">Subtotal</td>
                                            <td className="text-right w-20 py-1">
                                                {parseFloat(values?.subtotal || 0).toFixed(3)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-right pr-2 py-1">VAT</td>
                                            <td className="text-right py-1">
                                                {parseFloat(values?.taxamount || 0).toFixed(3)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-right pr-2 py-1">Discount</td>
                                            <td className="text-right py-1">
                                                {values?.discount
                                                    ? `-${parseFloat(values?.discount).toFixed(3)}`
                                                    : "0.000"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-right pr-2 py-2 font-semibold">
                                                Total (<UserCurrency />)
                                            </td>
                                            <td className="text-right py-2 font-semibold">
                                                {parseFloat(values?.total || 0).toFixed(3)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Deposits section */}
                    {values?.deposits?.length > 0 && (
                        <div className="border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
                            <div className="font-semibold text-[0.70rem] mb-3">Deposit Summary</div>
                            <table className="w-full text-[0.60rem]">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="text-left p-1 border-b border-gray-300">#</th>
                                        <th className="text-left p-1 border-b border-gray-300">Type</th>
                                        <th className="text-left p-1 border-b border-gray-300">Reference</th>
                                        <th className="text-left p-1 border-b border-gray-300">Date</th>
                                        <th className="text-left p-1 border-b border-gray-300">Note</th>
                                        <th className="text-right p-1 border-b border-gray-300">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {values.deposits.map((dep, i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="p-1">{i + 1}</td>
                                            <td className="p-1">{dep?.type || "—"}</td>
                                            <td className="p-1">{dep?.reference || "—"}</td>
                                            <td className="p-1">
                                                {dep?.date ? formatDateDDMMYYYY(dep?.date) : "—"}
                                            </td>
                                            <td className="p-1">{dep?.note || "—"}</td>
                                            <td className="text-right p-1">
                                                {parseFloat(dep?.amount || 0).toFixed(3)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={5} className="text-right pr-2 py-1 font-semibold text-[0.65rem]">
                                            Total Deposited
                                        </td>
                                        <td className="text-right py-1 font-semibold text-green-600">
                                            {totalDeposited.toFixed(3)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={5} className="text-right pr-2 py-1 font-semibold text-[0.65rem]">
                                            Remaining Balance
                                        </td>
                                        <td className="text-right py-1 font-semibold text-orange-600">
                                            {remaining.toFixed(3)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* Notes */}
                    {values?.note && (
                        <div className="border border-gray-300 rounded-lg p-3 mb-4 shadow-sm">
                            <div className="font-semibold text-[0.70rem] mb-1">Notes</div>
                            <div className="text-[0.65rem] text-gray-700">{values.note}</div>
                        </div>
                    )}

                    {/* Signatures */}
                    <div className="flex justify-between pt-10 text-[0.60rem]">
                        <div className="text-center">
                            <div className="border-b border-gray-400 w-32 mb-1"></div>
                            <div className="text-bold">Received By</div>
                        </div>
                        <div className="text-center">
                            <div className="border-b border-gray-400 w-32 mb-1"></div>
                            <div className="text-bold">Checked By</div>
                        </div>
                        <div className="text-center">
                            <div className="border-b border-gray-400 w-32 mb-1"></div>
                            <div className="text-bold">Prepared By</div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className="a4-booking" ref={ref}>
            <style>{`
        @media print {
          .a4-booking { page-break-before: always; }
          @page { size: A4; margin: 3% 6% 5% 6%; }
          .bkg-header-section { display: block !important; }
          .bkg-table-header-section { display: block !important; }
        }
        @media screen {
          .bkg-header-section { position: static; }
          .bkg-table-header-section { position: static; }
          .bkg-invoice-content { margin-top: 0; }
          .bkg-page-break { margin-top: 0; page-break-before: auto; }
          .bkg-invoice-table { table-layout: fixed; width: 100%; }
          .bkg-col-sno    { width: 5%; }
          .bkg-col-desc   { width: 25%; }
          .bkg-col-batch  { width: 12%; }
          .bkg-col-unit   { width: 6%; }
          .bkg-col-qty    { width: 6%; }
          .bkg-col-price  { width: 10%; }
          .bkg-col-vat    { width: 6%; }
          .bkg-col-vatamt { width: 12%; }
          .bkg-col-total  { width: 10%; }
        }
      `}</style>

            {renderHeaderSection()}
            {renderTableHeaders()}

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                const pageItems = getItemsForPage(pageNumber);
                const isLastPage = pageNumber === totalPages;
                return renderTableBody(pageItems, pageNumber, isLastPage);
            })}
        </div>
    );
});

export default A4Invoice;